/**
 * frontend/src/components/FileDropZone.ts
 * =========================================
 * Componente reutilizable de drag & drop para subida de un archivo.
 *
 * Uso:
 *   const dropZone = new FileDropZone({
 *     label: "Extracto Bancario",
 *     acceptedFormats: [".xlsx", ".csv"],
 *     acceptedMime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv",
 *     icon: "file",
 *     onFile: (file) => { ... }
 *   });
 *   container.appendChild(dropZone.element);
 *
 * Principio de independencia:
 *   No conoce la API, no conoce el estado global. Solo gestiona un archivo
 *   y notifica via callback. Fácil de reutilizar o reemplazar.
 *
 */

import { escapeHtml as _escapeHtml } from "../utils/texto";
import { icon, type IconName } from "../utils/icons";

export interface FileDropZoneOptions {
  label: string;
  hint: string;
  acceptedFormats: string[];   // ej: [".xlsx", ".csv"]
  acceptedMime: string;        // ej: "text/csv"
  icon: IconName;
  onFile: (file: File | null) => void;
  onError: (msg: string) => void;
}

export class FileDropZone {
  readonly element: HTMLElement;
  private _file: File | null = null;
  private _input: HTMLInputElement | null = null;
  private _opts: FileDropZoneOptions;
  /** Cancela todos los event listeners del ciclo anterior al re-render. */
  private _abortController = new AbortController();

  constructor(opts: FileDropZoneOptions) {
    this._opts = opts;
    this.element = this._build();
    this._input = this.element.querySelector("input");
    this._bindEvents();
  }

  get file(): File | null {
    return this._file;
  }

  /**
   * Resetea el componente al estado vacío.
   *
   * ORDEN CORRECTO:
   *   1. Limpiar _file
   *   2. _render() → reconstruye el template vacío (con <input>), actualiza this._input
   *   3. Limpiar el input recién creado
   *
   * El input se consulta después de reconstruir el marcado, nunca sobre el
   * nodo del ciclo anterior.
   */
  reset(): void {
    this._file = null;
    this._render();                               // rebuilds with <input>
    if (this._input) this._input.value = "";      // clear newly created input
  }

  private _build(): HTMLElement {
    const div = document.createElement("div");
    div.className = "dropzone";
    div.innerHTML = this._html();
    return div;
  }

  private _html(file?: File): string {
    if (file) {
      return `
        <div class="dropzone__content dropzone__content--loaded">
          <span class="dropzone__icon">${icon("file")}</span>
          <p class="dropzone__loaded-label">${icon("check")} Archivo cargado</p>
          <p class="dropzone__filename" title="${_escapeHtml(file.name)}">${_escapeHtml(file.name)}</p>
          <p class="dropzone__size">${_formatSize(file.size)} · ${_extension(file.name).slice(1).toUpperCase()}</p>
          <div class="dropzone__actions">
            <button class="btn btn--secondary btn--sm dropzone__change" type="button">${icon("edit")} Cambiar</button>
            <button class="btn btn--ghost btn--sm dropzone__remove" type="button">${icon("trash")} Eliminar</button>
          </div>
          <input type="file" class="dropzone__input" accept="${this._opts.acceptedFormats.join(",")}" aria-label="Cambiar ${_escapeHtml(this._opts.label)}" tabindex="-1" />
        </div>
      `;
    }
    return `
      <input
        type="file"
        class="dropzone__input"
        accept="${this._opts.acceptedFormats.join(",")}"
        aria-label="${_escapeHtml(this._opts.label)}"
      />
      <div class="dropzone__content">
        <span class="dropzone__icon">${icon(this._opts.icon)}</span>
        <span class="dropzone__choose">${icon("upload")} Seleccionar archivo</span>
        <p class="dropzone__formats">O arrástrelo aquí · ${this._opts.acceptedFormats.map(f => f.slice(1).toUpperCase()).join(" / ")}</p>
      </div>
    `;
  }

  private _render(): void {
    const teniaFoco = this.element.contains(document.activeElement);
    this.element.innerHTML = this._html(this._file ?? undefined);
    this.element.classList.toggle("dropzone--loaded", !!this._file);
    this.element.classList.remove("dropzone--error");
    this._input = this.element.querySelector("input");
    this._bindEvents();
    if (teniaFoco) {
      (this._file ? this.element.querySelector<HTMLButtonElement>(".dropzone__change") : this._input)?.focus();
    }
  }

  private _bindEvents(): void {
    // ── Cancelar listeners del ciclo anterior ─────────────────────────────────
    // Sin esto, cada _render() acumula una copia nueva de cada listener.
    this._abortController.abort();
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    // En una fuente cargada, solo Cambiar abre el selector de archivos.
    this.element.addEventListener("click", (e) => {
      if ((e.target as HTMLElement).closest(".dropzone__remove, .dropzone__input")) return;
      if (this._file && !(e.target as HTMLElement).closest(".dropzone__change")) return;
      this._input?.click();
    }, { signal });

    // File picker change
    this._input?.addEventListener("change", () => {
      const file = this._input?.files?.[0];
      if (file) this._handleFile(file);
    }, { signal });

    // Drag & drop
    this.element.addEventListener("dragover", (e) => {
      e.preventDefault();
      this.element.classList.add("dropzone--dragover");
    }, { signal });

    this.element.addEventListener("dragleave", () => {
      this.element.classList.remove("dropzone--dragover");
    }, { signal });

    this.element.addEventListener("drop", (e) => {
      e.preventDefault();
      this.element.classList.remove("dropzone--dragover");
      const file = e.dataTransfer?.files?.[0];
      if (file) this._handleFile(file);
    }, { signal });

    // Eliminar vuelve al estado vacío y notifica al contenedor.
    this.element
      .querySelector<HTMLButtonElement>(".dropzone__remove")
      ?.addEventListener("click", (e) => {
        e.stopPropagation();
        this.reset();
        this._opts.onFile(null);
      }, { signal });
  }

  private _handleFile(file: File): void {
    const ext = _extension(file.name);
    if (!this._opts.acceptedFormats.includes(ext)) {
      this.element.classList.add("dropzone--error");
      let aviso = this.element.querySelector<HTMLElement>(".dropzone__error");
      if (!aviso) {
        aviso = document.createElement("p");
        aviso.className = "dropzone__error";
        this.element.appendChild(aviso);
      }
      aviso.textContent = `Formato no admitido. Seleccione ${this._opts.acceptedFormats.map(f => f.slice(1).toUpperCase()).join(" / ")}.`;
      this._opts.onError(
        `Formato inválido para "${this._opts.label}". ` +
        `Se esperaba ${this._opts.acceptedFormats.join(" o ")} pero se recibió "${ext}".`
      );
      return;
    }
    this._file = file;
    this._render();
    this._opts.onFile(file);
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function _extension(filename: string): string {
  const dot = filename.lastIndexOf(".");
  return dot >= 0 ? filename.slice(dot).toLowerCase() : "";
}

function _formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
